import {Component, EventEmitter, Input, OnInit, Output, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {ClickService} from '../../../core/services/click.service';
import {ShadeToolsService} from '../../../core/services/shade-tools.service';
import {Device, DeviceConfig, DeviceId, DevicePatch, DeviceType} from '../../../shared/models/device';
import {EMPTY_ID} from '../../../shared/constants/empty-id';
import {Store} from '@ngrx/store';
import {AppState} from '../../../app/app.state';
import {DevicesService} from '../../../core/services/devices.service';
import {SaveRevertButtonsComponent, SaveRevertButtonsStatus} from '../save-revert-buttons/save-revert-buttons.component';
import {AutoClearSetting, AutoClearOptions} from '../../../shared/models/alarm';
import {Hub, HubId, HubType} from '../../../shared/models/hub';
import {Protocol} from '../../../shared/models/protocol';
import {Option} from '../../../shared/models/option';
import {HubsService} from '../../../core/services/hubs.service';
import {map} from 'rxjs/operators';
import {DrawerHeightFromBottomComponent} from '../drawer-height-from-bottom/drawer-height-from-bottom.component';
import {SpinnerComponent} from '../spinner/spinner.component';
import {PillComponent} from '../pill/pill.component';
import {PopoverSelectHubsComponent} from '../popover-multi-select/object-types/hubs/popover-select-hubs.component';
import {FormsModule} from '@angular/forms';
import { ProtocolPropertyLabel } from '../../../shared/constants/protocol-property-label.constants';


interface DeviceFormValues {
  Name?: string;
  Hub?: HubId;
  IpAddress?: string;
  Protocol?: DeviceType;
  Address?: string;
  AutoClear?: AutoClearSetting;
  AdvancedConfig?: string;
  BacnetMacAddress?: string;
  BacnetNetworkNumber?: string;
}

class ValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ValidationError';
  }
}

@Component({
  selector: 'motn-device-editor',
  templateUrl: './device-editor.component.html',
  styleUrls: ['./device-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    DrawerHeightFromBottomComponent,
    SpinnerComponent,
    PillComponent,
    PopoverSelectHubsComponent,
    FormsModule,
    SaveRevertButtonsComponent,
  ],
})
export class DeviceEditorComponent implements OnInit {
  @Input() deviceId: DeviceId;
  @Input() protocol: DeviceType; // ignored if deviceId provided
  @Output() saved$: EventEmitter<Device> = new EventEmitter<Device>();
  @Output() clickedOutside$: EventEmitter<null> = new EventEmitter<null>();

  /**
   * Property changes get stored here and potentially tweaked with just
   * before the PATCH call is made.
   *
   * The Config property in prePatch is a string; needs to be turned into
   * an actual object and converted to a DevicePatch before sending to API
   */
  changedFormValues: DeviceFormValues = {};
  initialFormValues: DeviceFormValues;

  device: Device;

  hubs: Hub[];
  hubOption: Option;
  hubMenuOpen: boolean = false;

  @ViewChild('saveRevertButtonsComponent') buttons: SaveRevertButtonsComponent;
  status: SaveRevertButtonsStatus;

  autoClearOptions = AutoClearOptions;


  constructor(
    private clickService: ClickService,
    private shadeTools: ShadeToolsService,
    private store: Store<AppState>,
    private devicesService: DevicesService,
    private hubsService: HubsService,
  ) {
  }


  ngOnInit() {
    if (this.deviceId) {
      this.devicesService.fetchDevice$(this.deviceId).subscribe({
        next: (device) => {
          this.device = JSON.parse(JSON.stringify(device));
          this.setInitialFormValues(this.device);

          // Only get hubs whose owners are same as this.device.Owner
          this.hubsService.getUserHubs$().pipe(
            map((hubs) => {
              return hubs.filter(h => h.Owner === this.device.Owner);
            }),
          ).subscribe({
            next: (hubs) => {
              this.hubs = this.filterHubsByProtocol(hubs);
            },
            error: () => {
              this.shadeTools.errorDialog();
            },
          });
        },
      });
    } else {
      // Get all hubs since this is a new device we're creating
      this.hubsService.getUserHubs$().subscribe({
        next: (hubs) => {
          this.hubs = this.filterHubsByProtocol(hubs);
        },
        error: () => {
          this.shadeTools.errorDialog();
        },
      });

      this.prepareNewDeviceObject();
    }
  }


  getAddressLabel(): string {
    switch (this.getProtocol()) {
      case 'bacnet':
        return ProtocolPropertyLabel.BACNET_INSTANCE_NUMBER;
      case 'modbus':
        return ProtocolPropertyLabel.MODBUS_ADDRESS;
      case 'lorawan':
        return 'DevEUI';
      default:
        return 'Address';
    }
  }


  getHubText(): string {
    if (!this.hubs) {
      return '';
    }

    if (this.changedFormValues.Hub) {
      const hub = this.hubs.find(h => h.ID === this.changedFormValues.Hub);
      return hub?.Name;
    }

    if (this.device) {
      const hub = this.hubs.find(h => h.ID === this.device.Hub);
      return hub?.Name;
    }

    return '';
  }


  getProtocol(): Protocol {
    let protocol: Protocol = this.changedFormValues.Protocol;

    if (!protocol) {
      protocol = this.initialFormValues.Protocol;
    }

    return protocol;
  }


  private prepareNewDeviceObject() {
    this.device = {
      Address: '',
      Config: {
        AutoClear: 'all',
        Policies: {},
      },
      Hub: EMPTY_ID,
      ID: EMPTY_ID,
      IP: '',
      Name: '',
      Owner: EMPTY_ID,
      Type: this.protocol,
    };

    this.setInitialFormValues(this.device);
  }


  private setInitialFormValues(device: Device) {
    this.initialFormValues = {
      Address: device.Address,
      AutoClear: device.Config.AutoClear,
      IpAddress: device.IP,
      Name: device.Name,
      Protocol: device.Type,
      AdvancedConfig: '',
      BacnetNetworkNumber: '',
      BacnetMacAddress: '',
    };

    this.prepareIpAddressForInput();
    this.prepareAdvancedSettingsForInput();
  }


  /**
   * This removes from the device config any settings that have dedicated settings inputs
   * in the component so that only items without dedicated inputs appear in the "Advanced
   * Settings" input.
   *
   * @private
   */
  private prepareAdvancedSettingsForInput() {
    const config: DeviceConfig = Object.assign({}, this.device.Config);
    delete config.AutoClear;

    this.initialFormValues.AdvancedConfig = JSON.stringify(config);
  }


  private prepareIpAddressForInput() {
    if (this.device.IP) {
      switch (this.device.Type) {
        case 'bacnet':
          const ipParts = this.device.IP.split('@');

          if (ipParts.length === 2) {
            const bacnetParts = ipParts[0].split(':');

            if (bacnetParts.length === 2) {
              this.initialFormValues.IpAddress = ipParts[1];
              this.initialFormValues.BacnetNetworkNumber = bacnetParts[0];
              this.initialFormValues.BacnetMacAddress = bacnetParts[1];
            }
          }

          break;
        case 'modbus':
          break;
      }

      if (!this.initialFormValues.IpAddress) {
        this.initialFormValues.IpAddress = this.device.IP;
      }
    }
  }


  /**
   * This block handles all settings that live in this.device.Config so the device
   * object can be properly saved back to the API.
   *
   * @private
   */
  private prepareDeviceConfigForApi(): DeviceConfig {
    let config: DeviceConfig;

    if (this.changedFormValues.AdvancedConfig) {
      config = JSON.parse(this.changedFormValues.AdvancedConfig);
    } else {
      config = JSON.parse(this.initialFormValues.AdvancedConfig);
    }

    if (this.changedFormValues.AutoClear !== undefined) {
      config.AutoClear = this.changedFormValues.AutoClear;
    } else {
      config.AutoClear = this.initialFormValues.AutoClear;
    }

    return config;
  }


  private prepareIpAddressForApi(): string {
    let ip = this.changedFormValues.IpAddress ?? this.initialFormValues.IpAddress;

    switch (this.getProtocol()) {
      case 'bacnet':
        const mac = this.changedFormValues.BacnetMacAddress ?? this.initialFormValues.BacnetMacAddress;
        const net = this.changedFormValues.BacnetNetworkNumber ?? this.initialFormValues.BacnetNetworkNumber;

        const defect1 = mac && !net;
        const defect2 = net && !mac;

        if (defect1 || defect2) {
          throw new ValidationError('If either a BACnet network ID or a BACnet MAC address is provided, then both values are required.');
        }

        const reHex: RegExp = /^(0x|0X)?[a-fA-F0-9]+$/;

        if (net && !reHex.test(net)) {
          throw new ValidationError('The BACnet network number entered is not formatted correctly. Only numbers and hex values are permitted.');
        }

        if (mac && !reHex.test(mac)) {
          throw new ValidationError('The BACnet MAC address entered is not formatted correctly. Only numbers and hex values are permitted.');
        }

        if (mac && net) {
          ip = `${net}:${mac}@${ip}`;
        }

        break;
      default:
    }

    return ip;
  }


  private prepareDeviceAddressForApi(): string {
    let address: string = '';

    switch (this.changedFormValues.Protocol) {
      case 'bacnet': {
        address = '' + this.changedFormValues.Address;

        break;
      }
      case 'modbus': {
        if (!/^[0-9]+$/.test('' + this.changedFormValues.Address)) {
          throw new ValidationError('The Modbus slave ID must be a number.');
        }

        address = '' + this.changedFormValues.Address;

        break;
      }
      default: {
        throw new ValidationError('No protocol is selected. Select a protocol and fill in the required inputs.');
      }
    }

    return address;
  }


  /**
   * For new devices, this will require all this.changes fields to be filled in properly.
   *
   * For existing devices, this will validate this.changes fields, if set, or this.devices fields
   * if the respective fields in this.changes are not set. Consequently, that means this will validate
   * values already stored to the API and so there is a chance that new validation rules imposed here
   * will prevent successful validation of previously accepted values. One exception: AdvancedConfig.
   *
   * This results in enforcement of new rules retroactively when editing existing devices.
   *
   * @private
   */
  private validateChanges() {
    {
      let name = this.changedFormValues.Name;

      if (!name) {
        name = this.initialFormValues.Name;
      }

      if (name.trim() === '') {
        throw new ValidationError('The device name cannot be blank.');
      }
    }

    {
      if ((this.protocol || this.device?.Type) !== DeviceType.LoRaWAN) {
        let ip = this.changedFormValues.IpAddress;

        if (!ip) {
          ip = this.initialFormValues.IpAddress;
        }

        if (!ip) {
          throw new ValidationError('An IP address must be entered.');
        }

        if (!/^((25[0-5]|(2[0-4]|1\d|[1-9]|)\d)\.?\b){4}(:\d{1,5})?$/.test(ip)) {
          throw new ValidationError('The IP address entered is not formatted correctly.');
        }
      }
    }

    {
      let address = this.changedFormValues.Address;

      if (!address) {
        address = this.initialFormValues.Address;
      }

      if (!/^[0-9]+$/.test('' + address)) {
        switch (this.getProtocol()) {
          case 'bacnet':
            throw new ValidationError('The BACnet instance ID must be a number.');
          case 'modbus':
            throw new ValidationError('The Modbus address must be a number.');
        }
      }
    }

    // This validation is unlike the others. Only the value stored in this.changes will
    // be checked.
    try {
      let advancedConfig = this.changedFormValues.AdvancedConfig;

      if (!advancedConfig) {
        advancedConfig = this.initialFormValues.AdvancedConfig;
      }

      if (advancedConfig) {
        JSON.parse(advancedConfig);
      }
    } catch (e) {
      throw new ValidationError('The value provided for Advanced Settings is invalid.');
    }
  }


  save() {
    try {
      this.validateChanges();

      if (this.deviceId) {
        this.patch();
      } else {
        this.create();
      }
    } catch (e: any) {
      if (e.name !== 'ValidationError') {
        throw(e);
      }
      this.shadeTools.errorDialog(e.message);
      this.buttons.resetStatus();
    }
  }


  private create() {
    if (!this.changedFormValues.Hub) {
      throw new ValidationError('A Motion Hub selection is missing.');
    }

    const protocol: Protocol = this.getProtocol();

    if (!protocol) {
      throw new ValidationError('A protocol must be selected.');
    }

    const device: Device = {
      Name: this.changedFormValues.Name,
      Type: protocol,
      Hub: this.changedFormValues.Hub,
      IP: this.prepareIpAddressForApi(),
      Address: this.changedFormValues.Address,
      Owner: this.hubs.find(h => h.ID === this.changedFormValues.Hub).Owner,
      Config: this.prepareDeviceConfigForApi(),
    } as Device;

    this.devicesService.createDevice$(device).subscribe({
      next: () => {
        this.shadeTools.simpleOkDialog('Success', 'The device was successfully added.');
        this.buttons.resetStatus();
        this.prepareNewDeviceObject();
        this.revert();
      },
      error: () => {
        this.shadeTools.errorDialog();
        this.buttons.resetStatus('edited');
      },
    });
  }


  private patch() {
    const patch: DevicePatch = {};

    if (this.changedFormValues.Hub) {
      patch.Hub = '' + this.changedFormValues.Hub;
    }

    if (this.changedFormValues.Name) {
      patch.Name = this.changedFormValues.Name;
    }

    if (this.device.IP !== this.prepareIpAddressForApi()) {
      patch.IP = this.prepareIpAddressForApi();
    }

    if (this.changedFormValues.Address) {
      patch.Address = this.changedFormValues.Address;
    }

    const deviceConfig = this.prepareDeviceConfigForApi();

    if (deviceConfig) {
      patch.Config = deviceConfig;
    }

    this.devicesService.patchDevice$(this.device.ID, patch).subscribe({
      next: (device) => {
        this.device = JSON.parse(JSON.stringify(device));
        this.buttons.showSavedStatus();
        this.saved$.next(this.device);
        this.changedFormValues = {};
        this.setInitialFormValues(this.device);
      },
      error: () => {
        this.shadeTools.errorDialog();
        this.buttons.resetStatus('edited');
      },
    });
  }


  revert() {
    this.setInitialFormValues(this.device);
    this.changedFormValues = {
      Hub: this.deviceId ? undefined : this.changedFormValues.Hub,
    };
  }


  updateStatus(status: SaveRevertButtonsStatus) {
    this.status = status;
  }


  openHubMenu() {
    this.hubMenuOpen = true;
  }


  onSelectHub($event: Option) {
    this.hubOption = $event;
    this.changedFormValues.Hub = $event.id;
    this.hubMenuOpen = false;
    this.updateStatus('edited');
  }


  private filterHubsByProtocol(hubs: Hub[]): Hub[] {
    const protocol = this.protocol || this.device?.Type;

    if (protocol === DeviceType.LoRaWAN) {
      return hubs.filter(h => h.Type === HubType.Cloud);
    } else if (protocol === DeviceType.BACnet || protocol === DeviceType.Modbus) {
      return hubs.filter(h => h.Type === HubType.OnPrem);
    }

    return hubs;
  }


  get protocolOptions(): Option[] {
    const protocol = this.protocol || this.device?.Type;

    if (protocol === DeviceType.LoRaWAN) {
      return [
        {
          id: 'lorawan',
          name: 'LoRaWAN',
        },
      ];
    } else if (protocol === DeviceType.BACnet || protocol === DeviceType.Modbus) {
      return [
        {
          id: '',
          name: '',
        },
        {
          id: 'bacnet',
          name: 'BACnet/IP',
        },
        {
          id: 'modbus',
          name: 'Modbus TCP',
        },
      ];
    }

    return [
      {
        id: '',
        name: '',
      },
      {
        id: 'bacnet',
        name: 'BACnet/IP',
      },
      {
        id: 'modbus',
        name: 'Modbus TCP',
      },
      {
        id: 'lorawan',
        name: 'LoRaWAN',
      },
    ];
  }

}
